import React, { useState, useRef } from 'react';
import {
  Flex, Text, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  step1Questions,
  worksheetInitialQuestions,
  maritalStatusOptions920,
  maritalStatusOptions1121,
  additionalAmountInitialQuestion,
} from './data';
import CollapsibleQuestions from '../CollapsibleQuestions';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function LegacyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  // // step one questions, fill in with answers:
  const step1Form = useRef();
  const step1InitQ = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitQ);

  const [numAllowances, setNumAllowances] = useState(data.allowances || 0);
  const [numAllowancesError, setNumAllowancesError] = useState(false);

  // fill in worksheet questions with answers:
  const [worksheetOpen, setWorksheetOpen] = useState(false);

  const worksheetForm = useRef();
  const worksheetInitQ = getInitialQuestions(worksheetInitialQuestions);
  const [worksheetQuestions, setWorksheetQuestions] = useState(worksheetInitQ);

  const allowancesForm = useRef();

  const getAllowancesQuestionsChange = (questions) => {
    if (
      questions[0].answer.answer.worksheet_filing_status !== ''
      || data.worksheet_filing_status
    ) {
      const fs = questions[0].answer.answer.worksheet_filing_status
        || data.worksheet_filing_status;
      let allowancesInitialQuestions;
      switch (version) {
        case '9_20':
          allowancesInitialQuestions = maritalStatusOptions920[fs];
          break;
        // There is no difference in figures for Version 11_21 and 11_22
        default:
          allowancesInitialQuestions = maritalStatusOptions1121[fs];
          break;
      }
      const allowancesQuestionWithAnswers = getInitialQuestions(
        allowancesInitialQuestions,
      );
      return allowancesQuestionWithAnswers;
    }
  };
  const [allowancesQuestions, setAllowancesQuestions] = useState(
    data.worksheet_filing_status
      ? getAllowancesQuestionsChange(worksheetQuestions)
      : [],
  );

  const numTrue = allowancesQuestions.reduce((acc, question) => {
    if (
      question.id !== 'form_part_2_line_17'
      && eval(Object.values(question.answer.answer)[0])
    ) acc += 1;
    return acc;
  }, 0);

  const line17 = allowancesQuestions.reduce((acc, question) => {
    if (question.id === 'form_part_2_line_17') {
      const value = Object.values(question.answer.answer)[0] || 0;
      acc += parseInt(value);
    }
    return acc || 0;
  }, 0);

  const calculatedNumAllowances = () => {
    const fs = worksheetQuestions[0].answer.answer.worksheet_filing_status;
    let acc = line17;

    if (numTrue == 0 && fs == 'surviving_spouse') {
      const allowance = getSurvivingSpouseAllNoAllowance(version);
      acc += allowance;
    }

    return acc || 0;
  };

  const getSurvivingSpouseAllNoAllowance = (version = '') => {
    switch (version) {
      case '9_20':
        return 4;
      default:
        return 5;
    }
  };

  // render marital status options multiple choice question
  const renderMaritalStatusOptions = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={allowancesQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = allowancesQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowancesQuestions(questions);
        }}
        ref={allowancesForm}
      />
    </Flex>
  );

  // render form from step 1 (fill in form)
  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
    </div>
  );

  const renderAllowances = (isReviewStep) => (
    <Flex column>
      <Flex column>
        <S.QuestionLabel isRequired={worksheetOpen} mb="7px">
          Allowances
        </S.QuestionLabel>
        <S.QuestionWrapper>
          <Flex>
            <S.Input
              type="text"
              value={
                  worksheetOpen ? calculatedNumAllowances() : numAllowances
                }
              onChange={(e) => setNumAllowances(e.target.value)}
              disabled={worksheetOpen}
            />
          </Flex>
        </S.QuestionWrapper>
        {numAllowancesError && (
        <Flex justifyContent="flex-start">
          <Text color="red">Please enter a valid response.</Text>
        </Flex>
        )}
      </Flex>
      <Flex column mt="20px">
        <CollapsibleQuestions
          title="If you need to calculate your total number of allowances, use this worksheet."
          handleClick={() => {
            if (!isReviewStep) {
              setWorksheetOpen(!worksheetOpen);
            }
          }}
          open={worksheetOpen || isReviewStep}
        >
          <FormConsumer
            questions={worksheetQuestions}
            {...(isReviewStep && {
              viewResultAsManager: true,
              reviewActions: [],
            })}
            onQuestionChange={({ question, value }) => {
              const questions = worksheetQuestions.map((q) => {
                if (q.id !== question.id) return q;
                const answer = {
                  ...q.answer,
                  answer: {
                    ...q.answer.answer,
                    [q.id]: value,
                  },
                };
                const updatedQuestion = { ...q, answer };
                return updatedQuestion;
              });
              setWorksheetQuestions(questions);
              setAllowancesQuestions(getAllowancesQuestionsChange(questions));
            }}
            ref={worksheetForm}
          />
          {worksheetQuestions[0].answer.answer.worksheet_filing_status
              !== '' && renderMaritalStatusOptions(isReviewStep)}
        </CollapsibleQuestions>
      </Flex>
    </Flex>
  );

  // answer additional amount question (question 2 on NC-4 form)
  const additionalAllowancesForm = useRef();
  const additionalAllowancesInitQ = getInitialQuestions(
    additionalAmountInitialQuestion,
  );
  const [additionalAmountQuestions, setAdditionalAmountQuestions] = useState(
    additionalAllowancesInitQ,
  );

  const renderAdditionalAllowances = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={additionalAmountQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalAmountQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalAmountQuestions(questions);
        }}
        ref={additionalAllowancesForm}
      />
    </Flex>
  );

  // show step one: fill the form
  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {/* render each part of step 1: */}
          {renderStep1Form()}
          {renderAllowances()}
          {renderAdditionalAllowances()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          // validate answers:
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (worksheetForm && worksheetForm.current) {
              worksheetForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowancesForm && allowancesForm.current) {
              allowancesForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (!/^\d*$/.test(numAllowances)) {
              err = true;
              setNumAllowancesError(true);
            } else {
              setNumAllowancesError(false);
            }
            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step
              let numAllowancesTemp;
              let wsData;
              let allowancesData;

              const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

              if (worksheetOpen) {
                numAllowancesTemp = calculatedNumAllowances;
                wsData = worksheetQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                allowancesData = allowancesQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              } else {
                numAllowancesTemp = numAllowances;
                wsData = worksheetInitQ.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                allowancesData = {};
              }
              const numAllowancesData = { allowances: numAllowancesTemp };
              const additionalAmountData = {
                additional_amount:
                  additionalAmountQuestions[0].answer.answer.additional_amount,
              };
              const data = {
                ...stepOneData,
                ...numAllowancesData,
                ...wsData,
                ...allowancesData,
                ...additionalAmountData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  // show step 2: review form with filled in information
  // isReviewStep = true, disable buttons
  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {renderAllowances(true)}
        {renderAdditionalAllowances(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  // show step 3: hellosign
  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default LegacyForm;
